/**
 * @file menu-form.ts
 * @description Форма пункта меню ↔ поля пункта (phase19.16 Р1; контракт §2;
 * проверка — `checkMenuNodeFields` пакета shared, повтор CHECK базы).
 *
 * Форма хранит одну «цель» независимо от вида; при сборке полей цель
 * попадает только в поле своего вида, остальные — `null` (смена вида
 * обнуляет цели других видов, как на сервере).
 */

import {
  checkMenuNodeFields,
  isSafeMenuUrl,
  SITE_MENU_TARGET_FIELD,
  type SiteMenuLinkType,
  type SiteMenuNodeFields,
  type SiteSystemRoute,
} from "@chaiberi/shared";

import type { SiteMenuItem } from "./types";

export interface MenuItemForm {
  linkType: SiteMenuLinkType;
  label: string;
  targetId: string | null;
  systemRoute: SiteSystemRoute | null;
  url: string;
  openInNewTab: boolean;
  autoLimit: number | null;
  isHidden: boolean;
}

/** Пустая форма нового пункта (страница сайта — самый частый вид). */
export function emptyMenuForm(linkType: SiteMenuLinkType = "SITE_PAGE"): MenuItemForm {
  return { linkType, label: "", targetId: null, systemRoute: null, url: "", openInNewTab: false, autoLimit: null, isHidden: false };
}

/** Пункт с сервера → форма правки. */
export function menuFormFromItem(item: SiteMenuItem): MenuItemForm {
  const field = item.linkType in SITE_MENU_TARGET_FIELD
    ? SITE_MENU_TARGET_FIELD[item.linkType as keyof typeof SITE_MENU_TARGET_FIELD]
    : null;
  return {
    linkType: item.linkType,
    label: item.label ?? "",
    targetId: field ? item[field] : null,
    systemRoute: item.systemRoute,
    url: item.url ?? "",
    openInNewTab: item.openInNewTab,
    autoLimit: item.autoLimit,
    isHidden: item.isHidden,
  };
}

/** Форма → поля пункта для сервера. */
export function menuFieldsFromForm(form: MenuItemForm): SiteMenuNodeFields {
  const fields: SiteMenuNodeFields = {
    label: form.label.trim() === "" ? null : form.label.trim(),
    linkType: form.linkType,
    sitePageId: null,
    categoryId: null,
    contentCategoryId: null,
    contentTagId: null,
    legalDocumentId: null,
    systemRoute: form.linkType === "SYSTEM" ? form.systemRoute : null,
    url: form.linkType === "URL" ? form.url.trim() : null,
    autoLimit: form.linkType === "CATALOG_CATEGORIES" ? form.autoLimit : null,
    openInNewTab: form.linkType === "URL" ? form.openInNewTab : false,
    isHidden: form.isHidden,
  };
  if (form.linkType in SITE_MENU_TARGET_FIELD) {
    const field = SITE_MENU_TARGET_FIELD[form.linkType as keyof typeof SITE_MENU_TARGET_FIELD];
    fields[field] = form.targetId;
  }
  return fields;
}

/** Ошибки формы для показа у полей: `label`, `target`, `url`, `autoLimit`. */
export type MenuFormErrors = Partial<Record<"label" | "target" | "url" | "autoLimit", string>>;

export function validateMenuForm(form: MenuItemForm): MenuFormErrors {
  const errors: MenuFormErrors = {};
  if (form.linkType in SITE_MENU_TARGET_FIELD && !form.targetId) errors.target = "Выберите, куда ведёт пункт";
  if (form.linkType === "SYSTEM" && !form.systemRoute) errors.target = "Выберите раздел";
  if (form.linkType === "URL" && !isSafeMenuUrl(form.url.trim())) {
    errors.url = "Адрес должен начинаться с https:// или быть адресом на этом сайте, например /about";
  }
  for (const issue of checkMenuNodeFields(menuFieldsFromForm(form), "item")) {
    if (issue.code === "site_menu_label_required" || issue.code === "site_menu_label_too_long") errors.label ??= issue.message;
    if (issue.code === "site_menu_auto_limit_invalid") errors.autoLimit ??= issue.message;
  }
  return errors;
}
